import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicShell from "@/components/PublicShell";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SparkleIcon as Sparkle, DownloadIcon as Download, ArrowRightIcon as ArrowRight, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";

/**
 * Public marketing-ready features inventory page (`/features`).
 *
 * Previously wrapped in `LegalLayout` (768px column meant for legal
 * docs) which made the stats strip + CTA buttons render awkwardly with
 * prose-inherited link underlines bleeding into the page chrome. Now
 * uses `PublicShell` with a wider container so the marketing content
 * gets the room it needs and the CTAs render as proper pill buttons
 * instead of styled prose links.
 *
 * Pulls the canonical list from `/api/features` so backend (PDF) and
 * frontend stay in lockstep. Hero grid surfaces the 15 differentiated
 * features; below is the categorized full inventory + stats + CTAs.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Features() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BACKEND}/api/features`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setErr(e?.message || "Failed to load"); });
    return () => { cancelled = true; };
  }, []);

  return (
    <PublicShell>
      <Helmet>
        <title>Features · Armstrong K9 Registry</title>
        <meta name="description" content="Everything Armstrong K9 Registry offers — verified breeders, audited pedigrees, Dog Log, live video tours, and more." />
      </Helmet>

      {/* HERO */}
      <section className="bg-white border-b border-[#EAEAEA]" data-testid="features-hero">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-dark mb-4">
            Platform · everything we offer
          </p>
          <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
            One registry, every tool a serious dog person needs.
          </h1>
          <p className="mt-5 text-[16px] leading-relaxed text-[#3F3F3F] max-w-2xl">
            A pedigree, breeder, marketplace, service-provider, transporter, and
            storefront platform built for buyers, breeders, trainers, groomers,
            walkers, sitters, transporters, shops, and the people who just love
            their dogs. Below are the{" "}
            <span className="font-semibold text-ink">
              {data?.unique?.length ?? "15"} features
            </span>{" "}
            that set us apart, followed by the full categorized inventory.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button asChild className="rounded-full bg-gold-gradient text-ink hover:opacity-95 border-0 h-12 px-6 font-semibold shadow-[0_8px_24px_rgba(201,162,39,0.35)]">
              <Link to="/billing" data-testid="features-pricing-link">
                See pricing &amp; upgrade <ArrowRight size={16} weight="bold" className="ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-ink text-ink hover:bg-ink hover:text-white h-12 px-6">
              <Link to="/register" data-testid="features-register-link">Create a free account</Link>
            </Button>
            <Button asChild variant="ghost" className="rounded-full h-12 px-5 text-[#3F3F3F] hover:text-ink hover:bg-cream">
              <a
                href={`${BACKEND}/api/features.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="features-pdf-btn"
              >
                <Download size={14} weight="bold" className="mr-1.5" /> One-page PDF
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* RECENTLY SHIPPED — explicit callout strip for the latest
          additions so visitors who only scan see what's new without
          having to spot updated items in the full inventory list. */}
      <section
        className="bg-gradient-to-br from-ink via-[#1a1a1a] to-ink text-white border-b border-ink/40"
        data-testid="features-recently-shipped"
      >
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="flex items-center gap-2 mb-3">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-gold opacity-75 animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-gold" />
            </span>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light">
              Recently shipped
            </p>
          </div>
          <h2 className="font-spectral font-light text-3xl sm:text-4xl tracking-tight leading-tight">
            What&rsquo;s new in 2026.
          </h2>
          <p className="mt-3 text-[14px] text-white/70 max-w-2xl leading-relaxed">
            The platform ships in weekly batches. Here&rsquo;s what landed in the
            last few iterations &mdash; full detail below in the inventory.
          </p>

          <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            <RecentlyShipped
              tag="Walkers &amp; sitters"
              title="Live GPS walk maps"
              blurb="Opt in per booking, see the dot move on a live map inside My Bookings while your dog is out. In-app only, 24-hour recap, then permanently deleted. Either side stops sharing in one tap."
              href="/help"
              testId="recently-shipped-gps"
            />
            <RecentlyShipped
              tag="Liability posture"
              title="Pure-venue dispute resolution"
              blurb="Disputes resolve by mutual written consent between the two parties or a published 30-day default-refund timeline. Armstrong administers the timer and the payment rails &mdash; never adjudicates the merits."
              href="/refund-policy"
              testId="recently-shipped-venue"
            />
            <RecentlyShipped
              tag="Payouts"
              title="Stripe Connect destination charges + 1099-K transparency"
              blurb="Every payout routes through Stripe destination charges so Stripe is the Payment Settlement Entity &mdash; sellers get one 1099-K from Stripe Express; Armstrong files nothing. Plain-English explainer + YTD banner."
              href="/help/1099-k-and-taxes"
              testId="recently-shipped-1099k"
            />
            <RecentlyShipped
              tag="Install"
              title="Browser-aware install page + shareable QR poster"
              blurb="One bookmarkable /install page routes iOS Safari, iOS Chrome, Android, and desktop visitors to the right install path. Download a branded 1200&times;1600 PNG QR poster &mdash; print it, email it, post it on social."
              href="/install"
              testId="recently-shipped-install"
            />
            <RecentlyShipped
              tag="Mobile UX"
              title="Pull-to-refresh + skeleton states"
              blurb="Native iOS-style pull-to-refresh on Bookings, Find Pros, Inbox, and Marketplace. Polished pulsing skeletons replace blank flashes so the page never collapses while data lands."
              href="/marketplace"
              testId="recently-shipped-ptr"
            />
            <RecentlyShipped
              tag="Provider trust"
              title="Optional insurance &amp; background-check badges"
              blurb="Identity verification is always required. Insurance, Pet First Aid, Breed-Specific Experience, and the paid background check are optional trust badges that lift your search rank &mdash; strongly recommended for trades where the dog leaves the owner&rsquo;s sight, but never required to list."
              href="/find-pros"
              testId="recently-shipped-badges"
            />
          </ul>
        </div>
      </section>

      {/* UNIQUE FEATURES GRID */}
      <section className="bg-[#FAFAFA] border-b border-[#EAEAEA]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="flex items-center gap-2 mb-8">
            <Sparkle size={18} weight="fill" className="text-gold" />
            <h2 className="font-spectral text-3xl sm:text-4xl tracking-tight">What sets us apart</h2>
          </div>
          {err && (
            <div className="text-sm text-crimson" data-testid="features-error">
              Could not load features list: {err}. Try refreshing.
            </div>
          )}
          {!data ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="features-grid-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-32 rounded-2xl" />
              ))}
            </div>
          ) : (
            <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="features-unique-grid">
              {data.unique.map((f, i) => (
                <li
                  key={f.title}
                  data-testid={`unique-feature-${i}`}
                  className="group relative bg-white border border-[#E5E5E5] rounded-2xl p-6 hover:border-gold hover:-translate-y-[2px] hover:shadow-[0_18px_36px_-16px_rgba(201,162,39,0.25)] transition-all overflow-hidden"
                >
                  <span className="absolute top-3 right-5 text-[11px] font-mono font-bold text-gold/60 tracking-wider">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="absolute left-0 top-0 bottom-0 w-1 bg-gold-gradient opacity-70 group-hover:opacity-100 transition-opacity" />
                  <h3 className="font-spectral text-lg tracking-tight pr-8 leading-tight mb-2">
                    {f.title}
                  </h3>
                  <p className="text-[13px] text-[#3F3F3F] leading-relaxed">
                    {f.blurb}
                  </p>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      {/* PERSONA FEATURE SHOWCASE — curated from the admin Feature Matrix */}
      <section className="bg-white border-b border-[#EAEAEA]" data-testid="features-personas">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-crimson">Built for every kind of dog person</p>
          <h2 className="font-spectral text-3xl sm:text-4xl tracking-tight mt-3 mb-10">
            One registry. Four ways to use it.
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                name: "Breeders & Kennel Owners",
                color: "#B8860B",
                tagline: "Run the whole program — pedigrees, heat cycles, litters, buyers, and payments — from one place.",
                items: [
                  "Register dogs with official Armstrong K9 Registry certificates",
                  "Multi-generation ancestry trees — pinch-to-zoom on mobile",
                  "Heat-cycle tracking with predicted-heat push reminders",
                  "Smart Matchmaker — data-driven stud & dam pairing suggestions",
                  "A public kennel page — your program's home on the internet",
                  "Stripe-powered payments straight to your bank",
                ],
              },
              {
                name: "Puppy Buyers & New Families",
                color: "#C1272D",
                tagline: "Find a verified breeder, dodge the scams, and count down to gotcha day.",
                items: [
                  "Directory of verified kennels with real pedigrees",
                  "Verified multi-generation pedigrees — see exactly what you're getting",
                  "Payments processed by Stripe — never wire money to a stranger",
                  "Go-home countdown pushes during the final week",
                  "The dog's full registry record transfers to you automatically",
                  "Chat with your breeder in-app — questions answered for life",
                ],
              },
              {
                name: "Everyday Dog Owners",
                color: "#2E6B34",
                tagline: "One app for your dog's whole life — health log, calendar, reminders, and trusted pros.",
                items: [
                  "One-tap daily logs — walk, meal, potty, meds, play",
                  "Dog Care Passport — one care profile every pro you book automatically sees",
                  "Every dog date in one calendar, synced to Google or Apple",
                  "Find background-checked groomers, walkers, sitters, and trainers nearby",
                  "Zoomie Time — book a private field by the hour for safe, off-leash play",
                  "Live GPS map while your dog is on a walk (with your consent)",
                  "Mid-walk photos pushed straight to your lock screen",
                ],
              },
              {
                name: "Groomers, Walkers & Sitters",
                color: "#8A5CB8",
                tagline: "A storefront, a booking book, and a repeat-business machine.",
                items: [
                  "Public provider page with services, pricing, photos, and reviews",
                  "Background-check badge that makes owners book with confidence",
                  "Instant online booking against your weekly availability",
                  "Payments go directly to your bank via Stripe",
                  "Recurring series, reschedules, and automatic session reminders",
                  "Every client's Dog Care Passport at your fingertips before the visit",
                ],
              },
            ].map((p) => (
              <div key={p.name} className="rounded-2xl border border-[#EAEAEA] p-7 shadow-luxury-light" data-testid={`features-persona-${p.name.split(" ")[0].toLowerCase().replace(/[^a-z]/g, "")}`}>
                <h3 className="font-spectral text-2xl tracking-tight" style={{ color: p.color }}>{p.name}</h3>
                <p className="text-sm text-[#6B6B6B] mt-1.5 leading-relaxed">{p.tagline}</p>
                <ul className="mt-5 space-y-2.5">
                  {p.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm leading-relaxed">
                      <CheckCircle size={16} weight="fill" className="flex-shrink-0 mt-0.5" style={{ color: p.color }} />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UPCOMING LITTERS — new 2026-05 product callout */}
      <section className="bg-white border-b border-[#EAEAEA]" data-testid="features-upcoming">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#8A6A00] mb-3">
                New · 2026
              </p>
              <h2 className="font-spectral text-3xl sm:text-4xl tracking-tight">
                Upcoming Litters & waitlist deposits
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-[#3F3F3F]">
                Publish a planned breeding the moment you make the match.
                Collect waitlist deposits directly to your Stripe Connect
                account. Host your lawyer-reviewed contract for two-party
                click-wrap signing. When puppies arrive, the listing
                auto-converts into a real litter with a placeholder dog
                row per puppy — ready to assign to each buyer with name,
                sex, and photo.
              </p>
              <ul className="mt-5 space-y-2 text-[13.5px] text-[#3F3F3F]">
                <li className="flex items-start gap-2"><CheckCircle size={15} weight="duotone" className="text-[#8A6A00] mt-0.5 shrink-0" /><span>Simple flat listing fee — see current rates on the Pricing page</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={15} weight="duotone" className="text-[#8A6A00] mt-0.5 shrink-0" /><span>Small capped platform fee on deposits</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={15} weight="duotone" className="text-[#8A6A00] mt-0.5 shrink-0" /><span>Breeder is merchant of record — chargebacks land on them, not Armstrong K9 Registry</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={15} weight="duotone" className="text-[#8A6A00] mt-0.5 shrink-0" /><span>Three audit-logged "venue, not a party" click-wraps (publish · deposit · sign)</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={15} weight="duotone" className="text-[#8A6A00] mt-0.5 shrink-0" /><span>8-week earliest-go-home rule auto-enforced</span></li>
              </ul>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-6">
                  <Link to="/litters/upcoming/new" data-testid="features-upcoming-cta">
                    Plan a litter <ArrowRight size={14} className="ml-1.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full h-11 px-6">
                  <Link to="/marketplace/upcoming" data-testid="features-upcoming-browse">
                    Browse upcoming litters
                  </Link>
                </Button>
              </div>
            </div>
            <div className="rounded-2xl border-2 border-[#C9A227] bg-[#FBF7EC] p-6">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A6A00] font-bold">
                How the money flows
              </p>
              <ol className="mt-3 space-y-3 text-[13.5px] text-ink leading-snug">
                <li><b>1.</b> Breeder publishes (flat listing fee → Armstrong K9 Registry).</li>
                <li><b>2.</b> Buyer pays deposit → goes <b>directly</b> to breeder's Stripe Connect account.</li>
                <li><b>3.</b> Armstrong K9 Registry takes a small capped fee on the deposit (the breeder sees their net immediately).</li>
                <li><b>4.</b> Refund policy is the breeder's own text. Armstrong never auto-refunds.</li>
                <li><b>5.</b> Disputes / chargebacks land on the breeder's Stripe — Armstrong K9 Registry is a venue, not a party.</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE VIDEO TOURS — discoverability section. Founder explicitly
          asked for a "where and how to start a video" callout on /features
          because the existing feature was buried in kennel profile edit. */}
      <section className="bg-ink text-white border-b border-ink" data-testid="features-video-tours">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="rounded-2xl border-2 border-gold/50 bg-[#0B0B0B] p-6 order-2 md:order-1">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold">
                How to start a video tour
              </p>
              <ol className="mt-3 space-y-3 text-[13.5px] leading-snug text-white/90">
                <li><b>1.</b> A buyer clicks <b>Request a Live Video Tour</b> on any of your dog's listings (or you launch one yourself from your kennel dashboard).</li>
                <li><b>2.</b> You get a request in your inbox — accept and pick a time.</li>
                <li><b>3.</b> Buyer pays the tour fee; the funds sit until the tour ends.</li>
                <li><b>4.</b> Join the secure browser room (no app installs — runs on Daily.co). Walk through the kennel, meet the dogs, answer questions live.</li>
                <li><b>5.</b> After the call ends, Armstrong releases <b>60–80%</b> of the fee straight to your Stripe (Pro: 75%, Elite: 80%).</li>
              </ol>
              <div className="mt-5 flex flex-wrap gap-2.5">
                <Button asChild className="rounded-full bg-gold-gradient text-ink hover:opacity-95 border-0 h-10 px-5 font-semibold">
                  <Link to="/tours" data-testid="features-video-tours-cta">
                    Open my video tours <ArrowRight size={13} className="ml-1.5" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white h-10 px-5">
                  <Link to="/marketplace" data-testid="features-video-tours-browse">
                    Browse breeders to tour
                  </Link>
                </Button>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-3">
                Earn while you sell
              </p>
              <h2 className="font-spectral text-3xl sm:text-4xl tracking-tight text-white">
                Live video kennel tours — keep up to 80% per tour.
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-white/70">
                Serious buyers want to see the dogs, the parents, and the home
                before they buy. The Live Video Tour turns that conversation
                into a paid 15-minute Daily.co session you can launch from
                any dog's listing or directly from your kennel dashboard. Buyers
                pay the tour fee up front; you keep the relationship, the listing,
                and most of the fee.
              </p>
              <ul className="mt-5 space-y-2 text-[13.5px] text-white/80">
                <li className="flex items-start gap-2"><CheckCircle size={15} weight="duotone" className="text-gold mt-0.5 shrink-0" /><span>One-tap launch — from your kennel dashboard or any listing</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={15} weight="duotone" className="text-gold mt-0.5 shrink-0" /><span>Flat buyer fee, zero setup, no app installs</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={15} weight="duotone" className="text-gold mt-0.5 shrink-0" /><span>Breeder cut: 60% Basic · 75% Pro · 80% Elite</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={15} weight="duotone" className="text-gold mt-0.5 shrink-0" /><span>Auto-payout to your Stripe after the call ends</span></li>
                <li className="flex items-start gap-2"><CheckCircle size={15} weight="duotone" className="text-gold mt-0.5 shrink-0" /><span>Marketplace badge advertising your tour earnings rate</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cream">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 text-center">
          <h2 className="font-spectral text-3xl sm:text-4xl tracking-tight mb-4">
            Ready to put your dog on the right side of the registry?
          </h2>
          <p className="text-[15px] text-[#3F3F3F] mb-7">
            Free to register dogs and litters via plan or one-off fees. Marketplace browsing is free for everyone.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild className="rounded-full bg-gold-gradient text-ink hover:opacity-95 border-0 h-12 px-7 font-semibold shadow-[0_8px_24px_rgba(201,162,39,0.35)]">
              <Link to="/billing" data-testid="features-cta-services">
                See pricing &amp; upgrade <ArrowRight size={16} weight="bold" className="ml-2" />
              </Link>
            </Button>
            <Button asChild className="rounded-full bg-ink hover:bg-crimson text-white h-12 px-7 font-semibold border-0">
              <Link to="/register" data-testid="features-cta-register">
                Create a free account <ArrowRight size={16} weight="bold" className="ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function RecentlyShipped({ tag, title, blurb, href, testId }) {
  const Card = (
    <div
      className="h-full rounded-2xl border border-white/15 bg-white/[0.04] hover:bg-white/[0.07] hover:border-gold/50 transition-colors p-5 sm:p-6"
      data-testid={testId}
    >
      <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-gold-light">
        {tag}
      </p>
      <h3 className="mt-1.5 font-spectral text-xl sm:text-[22px] leading-tight tracking-tight text-white">
        {title}
      </h3>
      <p
        className="mt-2.5 text-[13px] leading-relaxed text-white/75"
        dangerouslySetInnerHTML={{ __html: blurb }}
      />
    </div>
  );
  return (
    <li className="h-full">
      {href ? (
        <Link to={href} className="block h-full no-underline">
          {Card}
        </Link>
      ) : (
        Card
      )}
    </li>
  );
}
